import React from 'react';
import PropTypes from 'prop-types';

/**
 * It provides functionalities to maintain the order of the elements when changing the size
 * of the window, and to accommodate the different elements that are children of this one
 */
class Responsive extends React.Component {
    /**
     * Receive an arrangement with HTML elements and inform
     * if they are stacked or side by side
     */
    static areElementsStacked(elements) {
        return elements.reduce((prev, element) => {
            let { left } = element.getBoundingClientRect();
            left = Math.round(left);
            let { areStacked } = prev;
            if (left !== prev.left && prev.left !== null) {
                areStacked = false;
            }
            return { areStacked, left };
        }, { areStacked: true, left: null }).areStacked;
    }

    /**
     * Provides information about the window and the element that
     * can be obtained with the selector passed through parameters
     */
    static getClassAccordingToWidth(htmlNode) {
        let containerWidthClass;
        let containerWidth;
        if (htmlNode) {
            containerWidth = htmlNode.clientWidth;
            if (containerWidth <= 640) {
                containerWidthClass = 'kbe-parent-small';
            } else if (containerWidth <= 960) {
                containerWidthClass = 'kbe-parent-medium';
            } else {
                containerWidthClass = 'kbe-parent-large';
            }
        }

        return {
            windowWidth: window.innerWidth,
            containerWidth,
            containerWidthClass,
        };
    }

    /**
     * Values corresponding to the children are stored in childrenValues to manage
     * the comparison that can be requested by them, the property debounceTimeout
     * is used in the function that manages the resizes of the window
     */
    constructor(props) {
        super(props);

        const { container } = props;
        this.debounceTimeout = false;
        this.childrenValues = [];

        this.state = {
            ...Responsive.getClassAccordingToWidth(container),
            areChildrenStacked: false,
        };

        this.handleWindowResize = this.handleWindowResize.bind(this);
        this.isBelowFirstChild = this.isBelowFirstChild.bind(this);
    }

    /**
     * When the screen changes size, the values provided by the component
     * are recalculated
     */
    componentWillMount() {
        window.addEventListener('resize', this.handleWindowResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
    }

    /**
     * A debounce function is executed to maintain the performance and in each
     * iteration the parameters provided by the component are recalculated.
     */
    handleWindowResize() {
        clearTimeout(this.debounceTimeout);

        this.debounceTimeout = setTimeout(() => {
            const { container } = this.props;
            this.setState(Responsive.getClassAccordingToWidth(container));
        }, 250);
    }

    /**
     * An array of values is loaded as the children of the component are loaded,
     * and 'true' is returned in those whose value is less than the first.
     */
    isBelowFirstChild(index, element) {
        try {
            const elementClientRect = element.getBoundingClientRect();
            const totalHeightFromTop = Math.trunc(elementClientRect.top + elementClientRect.height);
            const distanceFromTop = Math.trunc(elementClientRect.top);

            this.childrenValues[index] = totalHeightFromTop;

            if (index !== 0 && distanceFromTop >= this.childrenValues[0]) {
                return true;
            }
            return false;
        } catch (e) {
            return false;
        }
    }

    /**
     * The component receives a function that (normally) returns a component which
     * has the possibility of receiving through the parameters of the function certain
     * values to manage changes according to the size of the window
     */
    render() {
        const { children } = this.props;
        const { isBelowFirstChild } = this;
        const { windowWidth, containerWidth, containerWidthClass, areChildrenStacked } = this.state;

        return children({
            windowWidth,
            containerWidth,
            containerWidthClass,
            isBelowFirstChild,
            areChildrenStacked,
        });
    }
}

Responsive.defaultProps = { container: null };

Responsive.propTypes = {
    children  : PropTypes.func.isRequired,
    container : PropTypes.oneOfType([
        PropTypes.instanceOf(Element),
        PropTypes.object,
    ]),
};

export default Responsive;
